
import { create } from 'zustand';
import { Post, PostsState, Comment } from '@/types/post';
import { generatePostId, generateRandomId } from '@/lib/utils';
import { sanitizeInput } from '@/utils/security';

// Initialize with some sample posts from localStorage or empty array
const getInitialPosts = (): Post[] => {
  const savedPosts = localStorage.getItem('posts');
  return savedPosts ? JSON.parse(savedPosts) : [];
};

export const usePostsStore = create<PostsState>((set, get) => ({
  posts: getInitialPosts(),
  isLoading: false,
  error: null,

  addPost: (postData) => {
    // Sanitize all text inputs
    const sanitizedPost = {
      ...postData,
      title: sanitizeInput(postData.title),
      content: sanitizeInput(postData.content),
      authorName: sanitizeInput(postData.authorName),
      tags: postData.tags.map(tag => sanitizeInput(tag)),
    };

    const newPost: Post = {
      ...sanitizedPost,
      id: generatePostId(),
      createdAt: new Date().toISOString(),
      likes: 0,
      comments: [],
    };

    set((state) => {
      const updatedPosts = [newPost, ...state.posts];
      // Persist to localStorage
      localStorage.setItem('posts', JSON.stringify(updatedPosts));
      return { posts: updatedPosts };
    });
  },

  likePost: (postId) => {
    set((state) => {
      const updatedPosts = state.posts.map(post => 
        post.id === postId ? { ...post, likes: post.likes + 1 } : post
      );
      localStorage.setItem('posts', JSON.stringify(updatedPosts));
      return { posts: updatedPosts };
    });
  },

  addComment: (postId, commentData) => {
    // Sanitize comment content
    const sanitizedContent = sanitizeInput(commentData.content);
    const sanitizedAuthorName = sanitizeInput(commentData.authorName);

    const newComment: Comment = {
      ...commentData,
      id: generateRandomId(),
      content: sanitizedContent,
      authorName: sanitizedAuthorName,
      createdAt: new Date().toISOString(),
      likes: 0,
    };

    set((state) => {
      const updatedPosts = state.posts.map(post => 
        post.id === postId 
          ? { ...post, comments: [newComment, ...post.comments] } 
          : post
      );
      localStorage.setItem('posts', JSON.stringify(updatedPosts));
      return { posts: updatedPosts };
    });
  },

  deletePost: (postId) => {
    set((state) => {
      const updatedPosts = state.posts.filter(post => post.id !== postId);
      localStorage.setItem('posts', JSON.stringify(updatedPosts));
      return { posts: updatedPosts };
    });
  },
}));
